import requester from '@/lib/axios';
import { VersioningStatusEnum } from '@/enums/VersioningStatus';
import { BusinessModelEnum, Course, CourseFormatEnum } from '@/models/Course';
import CoursePath from '@/models/CoursePath';
import JsonApiResponse from '@/models/JsonApiResponse';
import { BaseFilters } from '@/models/Service';
import { convertToFormData, createJsonBlob } from '@/utils/convertToFormData';
import { MULTIPART_FORM_DATA_HEADERS as headers } from '@/constants';
import { camelizeKeys } from 'humps';
import { EmptyActivity } from '@/models/_deprecated/Activity';
import { OrderingField } from '@/models/Filter';

export type CoursePathOrdering =
  | OrderingField<'version'>
  | OrderingField<'class_count'>
  | OrderingField<'created_at'>
  | OrderingField<'last_update'>
  | OrderingField<'rating'>;

export interface ListCoursePathFilters extends BaseFilters {
  formats?: CourseFormatEnum[];
  businessModels?: BusinessModelEnum[];
  courseLineId?: number[];
  moduleId?: number[];
  status?: VersioningStatusEnum[];
  uniqueCourse?: boolean;
  slug?: string;
  name?: string;
  courseId?: number[];
  ordering?: CoursePathOrdering;
  versionMin?: number[];
  versionMax?: number[];
}

export const listCoursesPath = async (params: ListCoursePathFilters = {}) => {
  const { data } = await requester().get<JsonApiResponse<CoursePath>>(
    'courses/',
    {
      params,
    },
  );

  return data;
};

export const getCoursePath = async (slugCourseName?: string) => {
  const { data } = await requester().get<CoursePath>(
    `courses/${slugCourseName}/`,
  );

  return data;
};

export type UpdateCoursePath = Partial<Pick<CoursePath, 'status' | 'isbnCode'>>;

export async function deleteCoursePath(slug: string) {
  await requester().delete(`courses/${slug}/`);
}

type CreateCoursePathPayload = Omit<Partial<CoursePath>, 'course'> & {
  course?: Partial<Course>;
};

export async function createCoursePath(
  fields: CreateCoursePathPayload,
): Promise<CoursePath> {
  const form = convertToFormData(fields, {
    course: (key, value, form) => {
      const { bannerImg, ...rest } = value;
      form.append(key, createJsonBlob(rest));
    },
  });

  if (fields.course?.bannerImg) {
    form.append('bannerImg', fields.course.bannerImg);
  }

  const { data } = await requester().post<CoursePath>(`courses/`, form, {
    headers,
  });

  data.course = camelizeKeys(JSON.parse(data.course.toString())) as Course;

  return data;
}

export async function updateCoursePath(
  slug: string,
  fields: Partial<CoursePath>,
) {
  const form = convertToFormData(fields, {
    course: (key, value, form) => {
      const { bannerImg, ...rest } = value;
      form.append(key, createJsonBlob(rest));
    },
  });

  if (fields.course?.bannerImg) {
    form.append('bannerImg', fields.course.bannerImg);
  }

  const { data } = await requester().patch<CoursePath>(
    `courses/${slug}/`,
    fields,
  );

  return data;
}

export async function getEmptyActivitiesBySlug(slug: string) {
  const { data } = await requester().get<EmptyActivity[]>(
    `courses/${slug}/check-activities/`,
  );

  return data;
}

export async function publishLessons(slug: string, lessons: number[]) {
  const { data } = await requester().post<CoursePath>(
    `courses/${slug}/publish/`,
    lessons.map(lessonId => ({ id: lessonId })),
  );

  return data;
}
